// ui/pages/CapacitacionFormPage.jsx  (modo edición si hay :uuid en la ruta)
// Página = carga (useCapacitacionForm) + cuerpo. El cuerpo se monta con la entidad ya cargada, así los editores
// (asistentes/imágenes) arrancan con sus valores iniciales sin efectos de sincronización.
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import BlockUi from 'react-block-ui';
import { useAuth } from '../../../../../components/content/auth.jsx';
import BackButton from '../../../../../components/link/back-button.jsx';
import { useCapacitacionForm } from '../../application/useCapacitacionForm';
import { useCapacitacionCatalogos } from '../../application/useCapacitacionCatalogos';
import { useAsistentesEditor } from '../../../shared/application/useAsistentesEditor';
import { useImagenesEditor } from '../../../shared/application/useImagenesEditor';
import { showAviso, showError, showSuccess } from '../../../shared/ui/feedback';
import { mensajeResultadoParcial, mensajesAgregarImagenes, soloExcedeLimite, MSG_ESPERAR_OPTIMIZACION } from '../../../shared/domain/media';
import { conCicloPorDefecto } from '../../../shared/domain/ciclo';
import { validarAsistentes } from '../../domain/capacitacion.rules';
import { idsAsistentesActivos } from '../../../shared/domain/asistentes';
import AsistentesSection from '../../../shared/ui/AsistentesSection.jsx';
import ImagenesSection from '../../../shared/ui/ImagenesSection.jsx';
import { guardarPendientesParaEdicion, tomarPendientes } from '../../../shared/ui/imagenesHandoff';
import CapacitacionForm from '../components/CapacitacionForm.jsx';
import { CAPACITACIONES_PATH } from '../../../constants';

// El backend reporta la actividad como id_actividad, id_actividades o id_actividades.N: todo va al campo del formulario.
const campoFormulario = field => (/^id_actividad(es(\.\d+)?)?$/.test(field) ? 'id_actividades' : field);

const listPath = `${CAPACITACIONES_PATH}/list`;

const CapacitacionFormBody = ({ data, isEdit, saving, save, reintentarImagen }) => {
  const history = useHistory();
  const { user } = useAuth();

  // Municipio elegido por el usuario; mientras no cambie se usa el de la entidad cargada (estado derivado).
  const [municipioElegido, setMunicipioElegido] = React.useState(undefined);
  const idMunicipio = municipioElegido !== undefined ? municipioElegido : data.id_municipio;
  const catalogos = useCapacitacionCatalogos({
    idMunicipio,
    idCooperativa: user ? user.id_cooperativa : undefined,
  });

  // Al crear se propone el ciclo activo (no hay campo en la UI); el formulario se monta hasta tener los ciclos.
  const initialValues = React.useMemo(() => (isEdit ? data : conCicloPorDefecto(data, catalogos.ciclos)), [isEdit, data, catalogos.ciclos]);
  const formReady = isEdit || catalogos.ciclosReady;

  // Los editores se montan cuando ya se conoce el ciclo (los productores se filtran por él).
  return formReady ? (
    <BlockUi tag="div" message="" blocking={catalogos.loading} renderChildren={true}>
      <FormWithEditors
        data={data}
        initialValues={initialValues}
        catalogos={catalogos}
        isEdit={isEdit}
        saving={saving}
        save={save}
        reintentarImagen={reintentarImagen}
        onMunicipioChange={setMunicipioElegido}
        history={history}
      />
    </BlockUi>
  ) : null;
};

const FormWithEditors = ({ data, initialValues, catalogos, isEdit, saving, save, reintentarImagen, onMunicipioChange, history }) => {
  const asistentes = useAsistentesEditor({
    initialIds: idsAsistentesActivos(data.asistentes),
    idCiclo: initialValues.id_ciclo,
  });
  // Imágenes que fallaron (o no llegaron a subirse) antes de pasar de crear a editar: ver shared/ui/imagenesHandoff.js.
  const [pendientesIniciales] = React.useState(() => tomarPendientes(data.uuid));
  const imagenes = useImagenesEditor({ saved: data.media, pendientes: pendientesIniciales });

  const handleRetry = item => reintentarImagen(item, imagenes.marcar);

  // El error de asistentes se muestra tras el primer intento de guardar y se limpia solo al agregar uno (derivado).
  const [intentado, setIntentado] = React.useState(false);
  const asistentesError = intentado ? validarAsistentes(asistentes.ids) : null;
  const asistentesRef = React.useRef(null);
  const validateExtra = () => {
    setIntentado(true);
    const message = validarAsistentes(asistentes.ids);
    if (message && asistentesRef.current)
      asistentesRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    return !message;
  };

  const handleFiles = async files => {
    const resultado = await imagenes.addFiles(files);
    const mensajes = mensajesAgregarImagenes(resultado);
    if (mensajes.length) (soloExcedeLimite(resultado) ? showAviso : showError)({ messages: mensajes });
  };

  const handleSubmit = async (values, setFormError) => {
    if (imagenes.procesando) {
      showAviso({ messages: [MSG_ESPERAR_OPTIMIZACION] });
      return;
    }
    const pendientes = imagenes.porSubir;
    const editPath = guardado => `${CAPACITACIONES_PATH}/edit/${guardado.uuid}`;
    try {
      const { guardado, imagenes: resultado, eliminaciones } = await save(values, {
        asistenteIds: asistentes.ids,
        pendientes,
        eliminaciones: imagenes.paraEliminar,
        onImagenEstado: imagenes.marcar,
        onEliminacionEstado: imagenes.marcarEliminacion,
      });
      if (resultado.fallidas.length || eliminaciones.fallidas.length) {
        // Datos guardados, imágenes no: se queda en la página. Al crear se pasa a editar (para no duplicar) llevando las fallidas.
        if (!isEdit) {
          const motivos = new Map(resultado.fallidas.map(f => [f.uuid, f.error]));
          guardarPendientesParaEdicion(
            guardado.uuid,
            pendientes.filter(p => motivos.has(p.uuid)).map(p => ({ ...p, estado: 'error', error: motivos.get(p.uuid) }))
          );
          history.replace(editPath(guardado));
        }
        await showError({
          message: mensajeResultadoParcial('La capacitación', {
            eliminar: eliminaciones.fallidas.length,
            subir: resultado.fallidas.length,
          }),
        });
        return;
      }
      showSuccess(isEdit ? 'Capacitación actualizada' : 'Capacitación creada');
      history.push(listPath);
    } catch (e) {
      if (e && e.fieldErrors) {
        Object.keys(e.fieldErrors).forEach(field => {
          setFormError(campoFormulario(field), { type: 'server', message: [].concat(e.fieldErrors[field]).join(' ') });
        });
      }
      // El padre ya existe (falló un paso posterior a guardarlo): al crear se va a su edición para reintentar sin duplicar,
      // llevando las imágenes aún pendientes (no se subió ninguna).
      if (e && e.guardado && !isEdit) {
        guardarPendientesParaEdicion(e.guardado.uuid, pendientes);
        history.replace(editPath(e.guardado));
      }
      await showError(e);
    }
  };

  return (
    <CapacitacionForm
      initialValues={initialValues}
      catalogos={catalogos}
      submitting={saving}
      onSubmit={handleSubmit}
      onCancel={() => history.push(listPath)}
      onMunicipioChange={onMunicipioChange}
      validateExtra={validateExtra}>
      <div ref={asistentesRef}>
        <AsistentesSection
          asistentes={asistentes.data}
          options={asistentes.options}
          loading={asistentes.status === 'loading'}
          disabled={saving}
          error={asistentesError}
          fotos={asistentes.fotos}
          onAdd={asistentes.add}
          onRemove={asistentes.remove}
        />
      </div>
      <ImagenesSection
        saved={imagenes.saved}
        pending={imagenes.data}
        total={imagenes.total}
        limiteAlcanzado={imagenes.limiteAlcanzado}
        disabled={saving}
        onFiles={handleFiles}
        entidad="capacitación"
        onRemove={imagenes.remove}
        onRetry={handleRetry}
        onPendingDone={imagenes.confirmarSubida}
        onMarkDelete={imagenes.marcarParaEliminar}
        onUndoDelete={imagenes.deshacerEliminar}
      />
    </CapacitacionForm>
  );
};

const CapacitacionFormPage = () => {
  const { uuid } = useParams();
  const { data, status, error, isEdit, saving, save, reintentarImagen } = useCapacitacionForm({ uuid });

  React.useEffect(() => {
    if (status === 'error') showError(error);
  }, [status, error]);

  return (
    <>
      <div className="mb-3">
        <BackButton label="Volver" goBackDefault={listPath} disabled={saving} />
      </div>
      <h1 className="page-header">{isEdit ? 'Editar Capacitación' : 'Registrar Capacitación'}</h1>
      <BlockUi tag="div" message="" blocking={status === 'loading'} renderChildren={true}>
        {data && <CapacitacionFormBody data={data} isEdit={isEdit} saving={saving} save={save} reintentarImagen={reintentarImagen} />}
      </BlockUi>
    </>
  );
};

export default CapacitacionFormPage;
